import { useState } from 'react';
import {
    Button,
    useNotify,
    useRefresh,
    useDataProvider,
    useTranslate,
    useListContext,
} from 'react-admin';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { Delete, ErrorOutline, CheckCircle, DeleteForever } from '@mui/icons-material';
import { SectionField } from '../../components';

const CustomBulkDeleteVMs = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();

    const { resource, selectedIds, onUnselectItems } = useListContext(); // ← list-local
    //   const resource = useResourceContext();                 // e.g. 'vms'

    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedVMs, setSelectedVMs] = useState([]);
    const [idsToDelete, setIdsToDelete] = useState([]);    // freeze IDs for the dialog/action

    const handleClick = async () => {
        const ids = selectedIds.length ? selectedIds : [];
        if (!selectedIds?.length) {
            notify(translate('app.vms.delete.noSelection') || 'No VMs selected', { type: 'warning' });
            return;
        }
        try {
            setIdsToDelete(ids);
            const { data } = await dataProvider.getMany(resource, { ids });
            setSelectedVMs(data || []);
            setOpenConfirm(true);
        } catch (error) {
            console.error(error);
            notify(translate('ra.notification.http_error'), { type: 'error' });
        }
    };

    const handleClose = () => setOpenConfirm(false);

    const handleDelete = async () => {
        try {
            await dataProvider.deleteMany(resource, { ids: idsToDelete });
            onUnselectItems();              // clears this list only
            notify(translate('ra.notification.deleted'), { type: 'success' });
            refresh(); // refetch list
        } catch (error) {
            console.error('[handleDelete] ❌ Error:', error);
            notify(`${translate('ra.notification.http_error')}: ${error.message}`, { type: 'error' });
        } finally {
            setOpenConfirm(false);
        }
    };

    return (
        <>
            <Button
                style={{ width: '100%', justifyContent: 'flex-start' }}
                label={translate('app.buttons.delete') || 'Delete'}
                onClick={handleClick}
                color="error"
            >
                <Delete sx={{ color: 'red !important' }} />
            </Button>

            <Dialog open={openConfirm} onClose={handleClose}>
                <DialogTitle><SectionField label={translate('app.vms.delete.confirmTitle', { smart_count: idsToDelete.length })}
                    icon={DeleteForever} /></DialogTitle>

                <DialogContent>
                    <Typography variant="headline2">
                        {translate('app.vms.delete.confirmMessage') || 'Do you really want to delete these VMs?'}
                    </Typography>

                    {selectedVMs.length > 0 && (
                        <ul style={{ marginLeft: '1em', paddingLeft: '1em', color: '#d32f2f', fontWeight: 'bold' }}>
                            {selectedVMs.map(vm => (
                                <li key={vm.id}>{vm.name || `VM-${vm.id}`}</li>
                            ))}
                        </ul>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} startIcon={<ErrorOutline />} label={translate('ra.action.cancel')} />
                    <Button onClick={handleDelete} startIcon={<CheckCircle />} label={translate('ra.action.confirm')} />
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CustomBulkDeleteVMs;
